import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { useAwards } from "../../../Pages.hook";
import { fields } from "../../../constants";

export const AddEditAwards = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const {
    data: editData,
    isLoading: drilldownLoading,
    refetch: refetchEditData,
  } = useAwards(query.get("editId") || "");

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEditData();
    }
  }, [query.get("editId"), query.get("id")]);

  const AwardsFormConfig: FormConfig = {
    initialValues: {
      awardsYear: decodeHTML(dataById ? dataById.payload.awardsYear : editData ? editData[0].awardsYear : ""),
      field_content_description_en: decodeHTML(
        dataById
          ? dataById.payload.field_content_description_en
          : editData
            ? editData[0].field_content_description_en
            : ""
      ),
      field_content_description_ms: decodeHTML(
        dataById
          ? dataById.payload.field_content_description_ms
          : editData
            ? editData[0].field_content_description_ms
            : ""
      ),
      field_content_description_zh: decodeHTML(
        dataById
          ? dataById.payload.field_content_description_zh
          : editData
            ? editData[0].field_content_description_zh
            : ""
      ),
      bannerImage: dataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "bannerImage.png",
              status: "done",
              url: dataById?.payload?.bannerImage,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "bannerImage.png",
              status: "done",
              url: editData?.[0]?.bannerImage,
            },
          ]),
      published: decodeHTML(dataById ? dataById.payload.published : editData ? editData[0].published : ""),
    },
    fields: fields.awardsList,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["bannerImage"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: "aboutEraman_awards_awardsList",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editData?.[0]?.id || dataById?.payload?.id }),
        ...data,
        // published: data.published, // Include published status
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status === "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          navigate(
            response.status === "OK" && status === "PENDING"
              ? "/dedicated/status-information?activeTab=pending"
              : "/dedicated/status-information?activeTab=draft"
          );
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status === "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          navigate(
            response.status === "OK" && status === "PENDING"
              ? "/dedicated/status-information?activeTab=pending"
              : "/dedicated/status-information?activeTab=draft"
          );
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Award`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingData || drilldownLoading}
        config={AwardsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") ? `${eramanHost}/api/awards-list-page-crud-api/${editData?.[0]?.id || dataById?.payload?.id}` : `${eramanHost}/api/create/awards-list-page-crud-api`}`,
            `${query.get("editId") ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") ? "PATCH" : "POST"}`,
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
